import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Dumbbell, Target, Users, Trophy } from 'lucide-react';
import { courses, testimonials, instagramPosts } from '../mockData';

const Home = () => {
  return (
    <div className="bg-[#1a1c1b] min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1688141402330-f4ed03f6bbf9"
            alt="MMA Training"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1c1b] via-[#1a1c1b]/80 to-transparent"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10 pt-24">
          <div className="max-w-3xl">
            <h1 className="text-[#d9fb06] font-black text-6xl md:text-8xl leading-none uppercase mb-6">
              Train Like a Fighter.
              <br />
              <span className="text-[#dfddd6]">Evolve Like</span> NexGen.
            </h1>
            <p className="text-[#dfddd6] text-xl md:text-2xl mb-8 max-w-2xl leading-relaxed">
              Elite MMA training for all levels. Build strength, discipline, and warrior mentality.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/book"
                className="bg-[#d9fb06] text-[#1a1c1b] px-8 py-4 rounded-full font-bold uppercase text-sm hover:opacity-90 transition-all hover:scale-105 inline-flex items-center justify-center"
              >
                Book Free Trial
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <Link
                to="/courses"
                className="border-2 border-[#d9fb06] text-[#d9fb06] px-8 py-4 rounded-full font-bold uppercase text-sm hover:bg-[#d9fb06] hover:text-[#1a1c1b] transition-all inline-flex items-center justify-center"
              >
                View Courses
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Disciplines Section */}
      <section className="py-20 bg-[#302f2c]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-[#d9fb06] font-black text-4xl md:text-5xl uppercase mb-4">
              Master Every Discipline
            </h2>
            <p className="text-[#888680] text-lg max-w-2xl mx-auto">
              From striking to grappling, we offer comprehensive training in all combat sports
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.slice(0, 6).map((course) => (
              <div
                key={course.id}
                className="group relative overflow-hidden bg-[#1a1c1b] border border-[#3f4816] hover:border-[#d9fb06] transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-[#d9fb06] font-bold text-xl uppercase mb-2">
                    {course.title}
                  </h3>
                  <p className="text-[#dfddd6] text-sm mb-4 line-clamp-2">
                    {course.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-[#888680] text-xs uppercase">
                      {course.levels.join(' • ')}
                    </span>
                    <Link
                      to="/courses"
                      className="text-[#d9fb06] text-sm font-semibold hover:underline"
                    >
                      Learn More →
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#1a1c1b]">
        <div className="container mx-auto px-6">
          <h2 className="text-[#d9fb06] font-black text-4xl md:text-5xl uppercase mb-12 text-center">
            Why NexGen MMA?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-[#3f4816] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Dumbbell size={36} className="text-[#d9fb06]" />
              </div>
              <h3 className="text-[#dfddd6] font-bold text-xl mb-2">Elite Training</h3>
              <p className="text-[#888680] text-sm">
                World-class coaches with professional fighting experience
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[#3f4816] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target size={36} className="text-[#d9fb06]" />
              </div>
              <h3 className="text-[#dfddd6] font-bold text-xl mb-2">All Levels</h3>
              <p className="text-[#888680] text-sm">
                Beginner-friendly programs to advanced competition prep
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[#3f4816] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={36} className="text-[#d9fb06]" />
              </div>
              <h3 className="text-[#dfddd6] font-bold text-xl mb-2">Community</h3>
              <p className="text-[#888680] text-sm">
                Join a supportive family of fighters and fitness enthusiasts
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[#3f4816] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy size={36} className="text-[#d9fb06]" />
              </div>
              <h3 className="text-[#dfddd6] font-bold text-xl mb-2">Results</h3>
              <p className="text-[#888680] text-sm">
                Proven track record of champions and transformed lives
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#302f2c]">
        <div className="container mx-auto px-6">
          <h2 className="text-[#d9fb06] font-black text-4xl md:text-5xl uppercase mb-12 text-center">
            Fighter Stories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-[#1a1c1b] border border-[#3f4816] p-6 hover:border-[#d9fb06] transition-colors"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-[#d9fb06] text-xl">★</span>
                  ))}
                </div>
                <p className="text-[#dfddd6] mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t border-[#3f4816] pt-4">
                  <p className="text-[#d9fb06] font-semibold">{testimonial.name}</p>
                  <p className="text-[#888680] text-sm">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Feed */}
      <section className="py-20 bg-[#1a1c1b]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-[#d9fb06] font-black text-4xl md:text-5xl uppercase mb-4">
              Follow Our Journey
            </h2>
            <a
              href="https://www.instagram.com/nexgenmmaclub/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#888680] hover:text-[#d9fb06] transition-colors text-lg"
            >
              @nexgenmmaclub
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {instagramPosts.map((post) => (
              <div
                key={post.id}
                className="aspect-square overflow-hidden group cursor-pointer"
              >
                <img
                  src={post.image}
                  alt={post.caption}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#3f4816] relative overflow-hidden">
        <div className="container mx-auto px-6 text-center relative z-10">
          <h2 className="text-[#d9fb06] font-black text-4xl md:text-6xl uppercase mb-6">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-[#dfddd6] text-xl mb-8 max-w-2xl mx-auto">
            Book your free trial class today and experience elite MMA training
          </p>
          <Link
            to="/book"
            className="bg-[#d9fb06] text-[#1a1c1b] px-10 py-5 rounded-full font-bold uppercase text-base hover:opacity-90 transition-all hover:scale-105 inline-flex items-center"
          >
            Book Free Trial Now
            <ArrowRight size={24} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
